import { css } from "@/domains/emotion";
import { MdsIcon } from "@/design-system/components/icon";
import { MdsIconKind } from "@/design-system/components/icon/types";
import styled from "@emotion/styled";
import { CollectionIcon } from "@/components/collection/CollectionIcon";
import { CollectionItemSubtitle } from "@/components/collection-item-subtitle";
import { CollectionItemMode } from "@/components/note/editor/top-bar/organize";
import { buildAsyncData } from "@/domains/async/AsyncData";
import { useTheme } from "@emotion/react";

// This component is used in the Organize dropdown to show Collections and
// the button to create a new Collection.

// TODO: This component is a 2-line menu-item component in Figma. We should use the React version when its available.

interface CollectionItemProps {
  collectionId: string;
  title: string;
  noteCount: number;
  subtitle?: string;
  isShared?: boolean;
  mode: CollectionItemMode;
  isSelected: boolean;
  isCollected: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const CollectionItem = ({
  collectionId,
  title,
  noteCount,
  subtitle,
  isShared,
  mode,
  onClick,
  isSelected,
  isCollected,
  onMouseEnter,
  onMouseLeave,
}: CollectionItemProps) => {
  const theme = useTheme();
  const sharingIconStyles = css({
    fontSize: "10px",
    color: theme.colors.icon.secondary,
  });
  return (
    <Wrapper
      isSelected={isSelected}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      mode={mode}
      role="button"
      aria-label={`${mode === "select" ? "Select collection: " + title : "Create collection: " + title}`}
      data-collection-item
    >
      <LeftSide>
        {mode === "select" && <CollectionIcon collectionId={collectionId} />}
        {mode === "create" && (
          <PlusIconWrapper>
            <MdsIcon kind={MdsIconKind.Plus} />
          </PlusIconWrapper>
        )}
        <Content>
          <TitleRow>
            <CollectionTitle>
              {mode === "create" && <>&ldquo;</>}
              {title}
              {mode === "create" && <>&rdquo;</>}
            </CollectionTitle>
            {isShared && (
              <SharedIconWrapper>
                <MdsIcon
                  kind={MdsIconKind.Shared}
                  innerStyles={{ Icon: { className: sharingIconStyles } }}
                />
              </SharedIconWrapper>
            )}
          </TitleRow>
          <CollectionItemSubtitle
            sizeData={buildAsyncData({ data: noteCount })}
            itemTypeName="note"
            description={subtitle}
          />
        </Content>
      </LeftSide>
      <RightSide>
        {isCollected ? (
          <MdsIcon
            kind={MdsIconKind.CheckCircleSolid}
            innerStyles={{ Icon: { className: css({ color: theme.colors.icon.selected }) } }}
          />
        ) : (
          <MdsIcon
            kind={MdsIconKind.Circle}
            innerStyles={{ Icon: { className: css({ color: theme.colors.icon.secondary }) } }}
          />
        )}
      </RightSide>
    </Wrapper>
  );
};

const CollectionTitle = styled.div(({ theme }) => ({
  fontStyle: "normal",
  fontSize: theme.fontSizes.small,
  lineHeight: theme.lineHeights.small,
  color: theme.colors.text.primary,
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
  minWidth: 0,
}));

const SharedIconWrapper = styled.div({
  flexShrink: 0,
});

const Wrapper = styled.div<{
  isSelected: boolean;
  mode: "select" | "create";
}>(({ theme, isSelected, mode }) => ({
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing.sm,
  paddingInline: theme.spacing["5"],
  paddingBlock: theme.spacing["2"],
  cursor: "pointer",
  borderRadius: theme.borderRadius.mediumLarge,
  backgroundColor: isSelected ? theme.colors.background.menu.hover : theme.colors.transparent,
  marginTop: mode === "create" ? theme.spacing.xs : undefined,
  width: "100%",
}));

const LeftSide = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing.sm,
  flexShrink: 1,
  minWidth: 0,
}));

const RightSide = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingRight: theme.spacing.sm,
  flexShrink: 0,
}));

const Content = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
  minWidth: 0,
}));

const TitleRow = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing[1],
  flex: 1,
  minWidth: 0,
}));

const PlusIconWrapper = styled.div({
  width: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
